import { createContext } from 'react';

import type { Tier as DreamTier } from '@shared/dream-components/src';

import type { ModalType } from '~/interfaces/modal_type';
import type { Publication } from '~/interfaces/publication';
import type { Subscription } from '~/interfaces/subscriptions';
import type { CurrentPlan } from '~/routes/subscribe/$subscriberId/_components/v2/hooks/useCurrentPlan';
import type { PlanChangePreview } from '~/services/api/subscription/getPlanChangePreview';

/**
 * Logic layer for the full subscription **lifecycle** surface on the General tab —
 * unsubscribe, resubscribe, report-abuse, downgrade (to free + per-tier), change-tier,
 * and account- / tier-level (un)pause — plus the current-plans display data those actions
 * hang off. Holds the single tier-selection state shared by every tier action, the proration
 * preview, and all the handlers, but renders no UI. Consumed by the colocated
 * `CurrentPlansSection` and `SubscriptionLifecycleModals` children. Billing data/payment
 * (invoices, payment method, admin billing) is a separate domain and stays out.
 */
export type SubscriptionActionsContextType = {
  // --- Openers (undefined when the action isn't available for the current state) ---
  onClickUnsubscribe?: () => void;
  onClickResubscribe?: () => void;
  onClickReportUnwantedSignup?: () => void;
  onClickUnpause?: () => void;
  onClickUnpausePlan: (tierId?: string) => void;
  onClickChangePlan: (tierId?: string) => void;
  onClickCancelPlan: (tierId?: string) => void;
  onClickUpgrade?: () => void;
  onClickLogin?: () => void;

  // --- Current-plans display (CurrentPlansSection) ---
  currentPlans: CurrentPlan[];
  showEmptyPlanState: boolean;
  isLoadingPlans: boolean;
  canShowCurrentPlans: boolean;
  hasGroupAndIndividualOverlap: boolean;
  groupSubscriptionsEnabled: boolean;
  groupAccessRevokedFrom: Subscription['group_access_revoked_from'];

  // --- Shared modal coordination ---
  currentModal: ModalType | null;
  closeModal: () => void;
  publication: Publication;
  subscriberId: string;
  isLoggedIn: boolean;

  // --- Unsubscribe / resubscribe / report-abuse / retention ---
  retentionFlowEnabled: boolean;
  isPaused: boolean;
  isPremiumTier: boolean;
  hasActivePaidTier: boolean;
  areAllPlansGifts: boolean;
  areAllPlansDowngrading: boolean;
  unsubscribeMessage: string;
  showUnsubscribeRetentionModal: boolean;
  resubscribeMessage: string;
  onUnsubscribeSuccess: () => void;

  // --- (Un)pause ---
  accountPauseId?: string;
  tierUnpauseTierId: string | null;
  tierUnpausePauseInfoId?: string;
  onUnpausePlanSuccess: () => void;

  // --- Change-plan ---
  changePlanTierId: string | null;
  actionableTier?: DreamTier;
  subscriptionPriceIds: string[];
  subscriptionTierIds: string[];
  selectedPriceId: string | null;
  onSelectPrice: (priceId: string) => void;
  prorationPreview?: PlanChangePreview;
  isLoadingProration: boolean;
  onChangePlanSuccess: () => void;

  // --- Downgrade ---
  downgradeMessage?: string;
  onDowngradeSuccess: () => void;
};

export const SubscriptionActionsContext = createContext<SubscriptionActionsContextType | undefined>(undefined);
SubscriptionActionsContext.displayName = 'SubscriptionActionsContext';
